import { Col, Flex, Row, Tooltip, Statistic } from 'antd';
import { useParams } from 'react-router';

import AccountOverview from './AccountOverview';

import ArchivedCreditOverview from './ArchivedCreditOverview';
import FraudStatusBanner from 'components/FraudStatusBanner/FraudStatusBanner';
import CreditAccountStatus from 'components/CreditAccountStatus/CreditAccountStatus';
import DisplayValue from 'components/DisplayValue/DisplayValue';
import useAnalytics from 'hooks/useAnalytics';

import { getDueDate, formatDate } from 'utilities/datesAndTimes';
import {
  ANALYTICS_EVENTS,
  SECURED_CARD_STATUS,
  STATUS,
} from 'utilities/constants';
import {
  formatDollarsFromCents,
  getCardDisplayName,
  getCreditProtectionDetails,
  minDueWarningHelper,
} from 'utilities/helpers';
import { creditAccountPath } from 'utilities/routes';

const CreditOverview = ({ account, simpleAccount }) => {
  const { customerId } = useParams();
  const { trackEvent } = useAnalytics();
  const formattedBalance = formatDollarsFromCents(
    account?.balanceInfo.currentBalance,
  );

  const { displayStatus: creditProtectionStatus } =
    getCreditProtectionDetails(account);

  const getAccountOverviewParams = () => {
    return {
      accountTitle: getCardDisplayName(account),
      cardLast4: `${account?.cardDetails?.last4}`,
      enableNavigation: true,
      navigateTo: creditAccountPath(customerId, account?.id),
      accountId: account?.id,
      onNavigate: () => {
        trackEvent(ANALYTICS_EVENTS.SNAPSHOT_CREDIT_OPENED);
      },
    };
  };

  const getMinDueValue = () => {
    let { shouldHighlightMinDue, minDueFormatted, highMinDueWarningMessage } =
      minDueWarningHelper(
        account?.paymentInfo.minimumDue,
        account?.balanceInfo.currentBalance,
      );

    let minDue = (
      <DisplayValue
        label="Total Min Due"
        status={shouldHighlightMinDue ? STATUS.ERROR : undefined}
      >
        {minDueFormatted}
      </DisplayValue>
    );

    if (shouldHighlightMinDue) {
      minDue = (
        <Tooltip title={highMinDueWarningMessage}>
          <div data-testid="min-due-tooltip-wrapper">{minDue}</div>
        </Tooltip>
      );
    }
    return minDue;
  };

  return (
    <AccountOverview {...getAccountOverviewParams()}>
      <Flex
        vertical={true}
        justify="center"
        gap={'var(--spacing-sm)'}
        style={{ height: '100%' }}
      >
        <FraudStatusBanner account={account} />
        <CreditAccountStatus
          account={account}
          simpleAccount={simpleAccount}
        ></CreditAccountStatus>
        <Statistic
          title="Account Balance"
          value={formattedBalance === '$NaN' ? undefined : formattedBalance}
        />
        <Row gutter={[10, '1rem']}>
          <Col sm={12}>
            <DisplayValue label="Available Credit">
              {formatDollarsFromCents(account?.balanceInfo.availableCredit)}
            </DisplayValue>
          </Col>
          <Col sm={12}>{getMinDueValue()}</Col>
          <Col sm={12}>
            <DisplayValue label="Due Date">
              {getDueDate(account?.paymentInfo)}
            </DisplayValue>
          </Col>
          <Col sm={12}>
            <DisplayValue label="Statement Cycle Date">
              {formatDate(account?.paymentInfo.nextStatementCloseDate)}
            </DisplayValue>
          </Col>
          <Col sm={12}>
            <DisplayValue label="Past Due Amount">
              {formatDollarsFromCents(account?.balanceInfo.pastDueBalance)}
            </DisplayValue>
          </Col>
          <Col sm={12}>
            <DisplayValue label="Credit Protection">
              {creditProtectionStatus}
            </DisplayValue>
          </Col>
          {account?.securedCardInfo ? (
            <>
              <Col sm={12}>
                <DisplayValue label="Current Secured Deposit Amount">
                  {formatDollarsFromCents(
                    account.securedCardInfo.currentAmount,
                  )}
                </DisplayValue>
              </Col>
              <Col sm={12}>
                <DisplayValue label="Original Secured Deposit Amount">
                  {formatDollarsFromCents(
                    account.securedCardInfo.originalAmount,
                  )}
                </DisplayValue>
              </Col>
              <Col sm={12}>
                <DisplayValue label="Security Deposit Status">
                  {SECURED_CARD_STATUS[account.securedCardInfo.status]}
                </DisplayValue>
              </Col>
              <Col sm={12}>
                <DisplayValue label="Date of Security Deposit Change">
                  {formatDate(account.securedCardInfo.refundedDate, 'N/A')}
                </DisplayValue>
              </Col>
            </>
          ) : null}
        </Row>
      </Flex>
    </AccountOverview>
  );
};

const ArchivalBoundary = ({ account, simpleAccount }) => {
  if (account?.archiveState || simpleAccount?.archiveState) {
    return <ArchivedCreditOverview account={account} />;
  } else {
    return <CreditOverview account={account} simpleAccount={simpleAccount} />;
  }
};

export default ArchivalBoundary;
